/**
 * Cart Subscribe & Save Component
 * Handles subscription intent through Recharge API
 */
class CartSubscribeSave extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.itemKey = this.dataset.itemKey;
    this.originalPrice = parseInt(this.dataset.price);
    this.hasSellingPlans = this.dataset.hasSellingPlans === 'true';
    this.isAlreadySubscribed = this.dataset.isSubscription === 'true';
    this.currentSellingPlan = this.dataset.currentSellingPlan || '';
    this.isProcessing = false;
    this.isSubscription = this.isAlreadySubscribed; // Check if already subscribed
    this.currentFrequency = '30'; // Default frequency

    // Get translated text from data attributes
    this.translations = {
      // Success messages
      successSubscribed: this.dataset.successSubscribed || 'Subscribed successfully!',
      successRemoved: this.dataset.successRemoved || 'Subscription removed!',
      successFrequencyUpdated: this.dataset.successFrequencyUpdated || 'Updated!',
      successSubscribedDescription: this.dataset.successSubscribedDescription || 'Recharge discount will be applied automatically at checkout.',
      successRemovedDescription: this.dataset.successRemovedDescription || 'This item will be purchased as a one-time order.',
      
      // UI text from data attributes - using getAttribute as fallback
      subscribe: this.dataset.textSubscribe || this.getAttribute('data-text-subscribe') || 'Subscribe',
      subscribed: this.dataset.textSubscribed || this.getAttribute('data-text-subscribed') || 'Subscribed',
      thirtyDays: this.dataset.text30Days || this.getAttribute('data-text-30-days') || '30 days',
      sixtyDays: this.dataset.text60Days || this.getAttribute('data-text-60-days') || '60 days',
      thirtyDaysDiscount: this.dataset.text30DaysDiscount || this.getAttribute('data-text-30-days-discount') || '30 days (15%)',
      sixtyDaysDiscount: this.dataset.text60DaysDiscount || this.getAttribute('data-text-60-days-discount') || '60 days (10%)',
      switching: this.dataset.textSwitching || this.getAttribute('data-text-switching') || 'Loading...'
    };

    // Parse selling plans data
    if (this.dataset.sellingPlans) {
      this.sellingPlans = this.parseSellingPlans(this.dataset.sellingPlans);
    } else {
      this.sellingPlans = {};
    }

    console.log('🎯 CartSubscribeSave initialized with translations:', this.translations);
    console.log('🔍 Data attributes received:', {
      textSubscribe: this.dataset.textSubscribe,
      textSubscribed: this.dataset.textSubscribed,
      text30Days: this.dataset.text30Days,
      text60Days: this.dataset.text60Days,
      text30DaysDiscount: this.dataset.text30DaysDiscount,
      text60DaysDiscount: this.dataset.text60DaysDiscount
    });
    console.log('🔍 HTML attributes check:', {
      'data-text-30-days': this.getAttribute('data-text-30-days'),
      'data-text-60-days': this.getAttribute('data-text-60-days'),
      'data-text-subscribe': this.getAttribute('data-text-subscribe'),
      'data-text-subscribed': this.getAttribute('data-text-subscribed')
    });
    console.log('🔍 Current HTML content:', this.innerHTML.substring(0, 200) + '...');
    console.log('🎯 Selling plans parsed:', this.sellingPlans);
    console.log('🎯 Is already subscribed:', this.isAlreadySubscribed);
    console.log('🎯 Current selling plan:', this.currentSellingPlan);

    // If already subscribed, detect the frequency from the selling plan
    if (this.isAlreadySubscribed && this.currentSellingPlan) {
      this.currentFrequency = this.detectFrequencyFromSellingPlan(this.currentSellingPlan) || '30';
      console.log('🎯 Detected frequency from selling plan:', this.currentFrequency);
    }

    // Always update the frequency display with translated text during initialization
    this.updateFrequencyDisplay();

    this.checkSubscriptionStatus();
    this.bindEvents();
  }

  /**
   * Parse selling plans data from the format "id1:name1,id2:name2"
   */
  parseSellingPlans(sellingPlansData) {
    const plans = {};
    if (!sellingPlansData) return plans;

    sellingPlansData.split(',').forEach(planData => {
      const [id, name] = planData.split(':');
      if (id && name) {
        // Try to detect frequency from plan name
        const frequency = this.detectFrequencyFromPlanName(name);
        if (frequency) {
          plans[frequency] = id;
        }
      }
    });

    return plans;
  }

  /**
   * Detect frequency from selling plan name
   */
  detectFrequencyFromPlanName(planName) {
    const name = planName.toLowerCase();
    console.log('🔍 Detecting frequency from plan name:', planName, '→', name);
    
    if (name.includes('30') || name.includes('month')) {
      console.log('✅ Detected 30-day frequency');
      return '30';
    } else if (name.includes('60') || name.includes('2 month') || name.includes('two month')) {
      console.log('✅ Detected 60-day frequency');
      return '60';
    }
    
    console.log('⚠️ Could not detect frequency from plan name:', planName);
    return null;
  }

  /**
   * Detect current frequency from current selling plan ID
   */
  detectFrequencyFromSellingPlan(sellingPlanId) {
    console.log('🔍 Detecting frequency from selling plan ID:', sellingPlanId);
    console.log('🔍 Available selling plans:', this.sellingPlans);
    
    for (const [frequency, planId] of Object.entries(this.sellingPlans)) {
      console.log(`🔍 Checking ${frequency}: ${planId} === ${sellingPlanId}`);
      if (planId === sellingPlanId) {
        console.log(`✅ Found matching frequency: ${frequency}`);
        return frequency;
      }
    }
    
    console.log('⚠️ Could not find frequency for selling plan ID:', sellingPlanId, 'defaulting to 30');
    return '30'; // Default fallback
  }

  bindEvents() {
    console.log('🔗 bindEvents called');
    
    // Remove existing event listeners to prevent duplicates
    const checkbox = this.querySelector('.cart-subscribe-save__checkbox');
    const frequencyToggle = this.querySelector('.cart-subscribe-save__frequency-toggle');
    const frequencyOptions = this.querySelectorAll('.cart-subscribe-save__frequency-option');

    if (checkbox) {
      // Remove existing listener if any
      checkbox.removeEventListener('change', this.handleCheckboxChange);
      
      // Bind checkbox change event
      this.handleCheckboxChange = async (e) => {
        console.log('📦 Checkbox changed:', e.target.checked);
        if (this.isProcessing) {
          console.log('⏳ Already processing, ignoring checkbox change');
          return;
        }
        await this.handleSubscriptionToggle(e.target.checked);
      };
      
      checkbox.addEventListener('change', this.handleCheckboxChange);
      console.log('✅ Checkbox event listener bound');
    }

    if (frequencyToggle) {
      // Remove existing listener if any
      frequencyToggle.removeEventListener('click', this.handleFrequencyToggleClick);
      
      // Bind frequency toggle click event
      this.handleFrequencyToggleClick = (e) => {
        console.log('🔄 Frequency toggle clicked');
        e.preventDefault();
        e.stopPropagation();
        this.toggleFrequencyDropdown();
      };
      
      frequencyToggle.addEventListener('click', this.handleFrequencyToggleClick);
      console.log('✅ Frequency toggle event listener bound');
    } else {
      console.warn('⚠️ Frequency toggle not found for event binding');
    }

    // Bind frequency option events
    frequencyOptions.forEach(option => {
      // Remove existing listener if any
      option.removeEventListener('click', option.handleOptionClick);
      
      option.handleOptionClick = async (e) => {
        console.log('🎯 Frequency option clicked:', e.target.dataset.frequency);
        e.preventDefault();
        e.stopPropagation();
        
        const newFrequency = e.target.dataset.frequency;
        if (newFrequency && newFrequency !== this.currentFrequency) {
          await this.handleFrequencyChange(newFrequency);
        }
        
        this.hideFrequencyDropdown();
      };
      
      option.addEventListener('click', option.handleOptionClick);
    });
    
    console.log(`✅ Bound events for ${frequencyOptions.length} frequency options`);

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.hideFrequencyDropdown();
      }
    });
  }

  /**
   * Toggle the frequency dropdown visibility
   */
  toggleFrequencyDropdown() {
    console.log('🔄 toggleFrequencyDropdown called');
    const dropdown = this.querySelector('.cart-subscribe-save__frequency-dropdown');
    if (!dropdown) {
      console.warn('⚠️ Frequency dropdown not found');
      return;
    }
    
    const isVisible = dropdown.style.display !== 'none';
    console.log('🔍 Dropdown currently visible:', isVisible);
    
    if (isVisible) {
      this.hideFrequencyDropdown();
    } else {
      this.showFrequencyDropdown();
    }
  }

  /**
   * Show the frequency dropdown
   */
  showFrequencyDropdown() {
    console.log('📖 showFrequencyDropdown called');
    const dropdown = this.querySelector('.cart-subscribe-save__frequency-dropdown');
    if (dropdown) {
      dropdown.style.display = 'block';
      
      // Update active state for current frequency
      this.updateDropdownActiveState();
      console.log('✅ Frequency dropdown shown');
    } else {
      console.warn('⚠️ Frequency dropdown not found');
    }
  }

  /**
   * Hide the frequency dropdown
   */
  hideFrequencyDropdown() {
    const dropdown = this.querySelector('.cart-subscribe-save__frequency-dropdown');
    if (dropdown) {
      dropdown.style.display = 'none';
      console.log('✅ Frequency dropdown hidden');
    }
  }

  /**
   * Update the active state in dropdown options
   */
  updateDropdownActiveState() {
    const frequencyOptions = this.querySelectorAll('.cart-subscribe-save__frequency-option');
    frequencyOptions.forEach(option => {
      option.classList.toggle('active', option.dataset.frequency === this.currentFrequency);
    });
  }

  async handleSubscriptionToggle(isChecked) {
    if (this.isProcessing) return;

    console.log(`🔄 Subscription toggle: ${isChecked ? 'Adding subscription' : 'Removing subscription'}`);

    // If checking the box: Create subscription intent
    if (!this.isSubscription && isChecked) {
      await this.createSubscription();
      return;
    }

    // If unchecking the box: Remove subscription intent
    if (this.isSubscription && !isChecked) {
      await this.removeSubscription();
      return;
    }
  }

  async createSubscription() {
    try {
      this.setLoadingState(true);
      
      console.log('🔄 Creating subscription with selling plan');

      // Get the selling plan ID for the current frequency
      const sellingPlanId = this.sellingPlans[this.currentFrequency];
      
      if (!sellingPlanId) {
        throw new Error(`No selling plan found for ${this.currentFrequency} day frequency`);
      }

      // Get the most current quantity right before the API call
      const currentQuantity = await this.getCurrentQuantity();
      console.log('🔍 Current quantity for subscription creation:', currentQuantity);

      // Create subscription through Recharge API
      const result = await window.RechargeCartProxy.convertToSubscription({
        variantId: this.variantId,
        quantity: currentQuantity,
        frequency: this.currentFrequency,
        itemKey: this.itemKey,
        sellingPlanId: sellingPlanId
      });
      
      console.log('✅ Subscription created with selling plan:', result);
      
      // Update UI immediately
      this.isSubscription = true;
      this.updateSubscriptionDisplay();
      this.updateFrequencyDisplay();
      
      // Show brief success message
      this.showSuccess();
      
      // Quick cart refresh
      setTimeout(() => {
        this.refreshCart();
      }, 500);

    } catch (error) {
      console.error('❌ Subscription creation failed:', error);
      
      // Reset checkbox state
      const checkbox = this.querySelector('.cart-subscribe-save__checkbox');
      if (checkbox) checkbox.checked = false;
      this.isSubscription = false;
      
      this.showError(error.message);
    } finally {
      this.setLoadingState(false);
    }
  }

  async removeSubscription() {
    try {
      this.setLoadingState(true);
      console.log('🔄 Converting subscription back to one-time purchase');
      
      // Get the most current quantity right before the API call
      const currentQuantity = await this.getCurrentQuantity();
      console.log('🔍 Current quantity for subscription removal:', currentQuantity);
      
      // Remove subscription through Recharge API
      await window.RechargeCartProxy.removeSubscription({
        itemKey: this.itemKey,
        variantId: this.variantId,
        quantity: currentQuantity
      });
      
      // Update UI immediately
      this.isSubscription = false;
      
      // Show brief success message
      this.showOneTimeSuccess();
      
      // Quick cart refresh
      setTimeout(() => {
        this.refreshCart();
      }, 500);

    } catch (error) {
      console.error('❌ Subscription removal failed:', error);
      
      // Reset checkbox state back to checked
      const checkbox = this.querySelector('.cart-subscribe-save__checkbox');
      if (checkbox) checkbox.checked = true;
      
      this.showError('Failed to remove subscription');
    } finally {
      this.setLoadingState(false);
    }
  }

  async handleFrequencyChange(newFrequency) {
    console.log('🎯 handleFrequencyChange called with:', newFrequency);
    console.log('🔍 Current state check:');
    console.log('  - isProcessing:', this.isProcessing);
    console.log('  - currentFrequency:', this.currentFrequency);
    console.log('  - newFrequency:', newFrequency);
    console.log('  - isSubscription:', this.isSubscription);
    console.log('  - sellingPlans:', this.sellingPlans);
    
    if (this.isProcessing || newFrequency === this.currentFrequency || !this.isSubscription) {
      console.warn('⚠️ Frequency change blocked:');
      console.warn('  - isProcessing:', this.isProcessing);
      console.warn('  - same frequency?', newFrequency === this.currentFrequency);
      console.warn('  - not subscribed?', !this.isSubscription);
      return;
    }

    console.log(`🔄 Changing subscription frequency from ${this.currentFrequency} to ${newFrequency} days`);
    
    this.currentFrequency = newFrequency;
    this.updateFrequencyDisplay();

    // Update the subscription frequency through Recharge API
    await this.updateSubscriptionFrequency(newFrequency);
  }

  async updateSubscriptionFrequency(newFrequency) {
    try {
      this.setLoadingState(true);
      
      console.log('🔄 Updating subscription frequency with new selling plan:', newFrequency);

      // Get the selling plan ID for the new frequency
      const newSellingPlanId = this.sellingPlans[newFrequency];
      
      if (!newSellingPlanId) {
        throw new Error(`No selling plan found for ${newFrequency} day frequency`);
      }

      // Get the most current quantity right before the API call
      const currentQuantity = await this.getCurrentQuantity();
      console.log('🔍 Current quantity for frequency update:', currentQuantity);

      // Update subscription frequency through Recharge API
      const result = await window.RechargeCartProxy.updateSubscriptionFrequency({
        frequency: newFrequency,
        itemKey: this.itemKey,
        variantId: this.variantId,
        quantity: currentQuantity,
        newSellingPlanId: newSellingPlanId
      });
      
      console.log('✅ Subscription frequency updated with new selling plan:', result);
      
      // Update UI immediately
      this.currentFrequency = newFrequency;
      this.updateFrequencyDisplay();
      
      // Show brief success message
      this.showFrequencyUpdateSuccess();
      
      // Quick cart refresh (no delay)
      setTimeout(() => {
        this.refreshCart();
      }, 500); // Much shorter delay - just enough to see success message

    } catch (error) {
      console.error('❌ Frequency update failed:', error);
      
      // Reset frequency display to previous value
      const previousFrequency = this.currentFrequency === '30' ? '60' : '30';
      this.currentFrequency = previousFrequency;
      this.updateFrequencyDisplay();
      
      this.showError(`Failed to update delivery frequency: ${error.message}`);
    } finally {
      this.setLoadingState(false);
    }
  }

  /**
   * Get the current quantity of this cart item from multiple sources
   */
  async getCurrentQuantity() {
    // Method 1: Try to get from quantity input in the DOM (most immediate)
    const quantityInput = document.querySelector(`[data-key="${this.itemKey}"] input[name="updates[]"]`);
    if (quantityInput && quantityInput.value) {
      const domQuantity = parseInt(quantityInput.value) || 1;
      console.log('🔍 Quantity from DOM input:', domQuantity);
      return domQuantity;
    }

    // Method 2: Try to get from cart data attribute
    const cartItem = document.querySelector(`[data-key="${this.itemKey}"]`);
    if (cartItem) {
      const dataQuantity = cartItem.dataset.quantity;
      if (dataQuantity) {
        const attrQuantity = parseInt(dataQuantity) || 1;
        console.log('🔍 Quantity from data attribute:', attrQuantity);
        return attrQuantity;
      }
    }

    // Method 3: Fetch current cart and find this item
    try {
      console.log('🔍 Fetching current cart to get quantity...');
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        const cartItem = cart.items.find(item => item.key === this.itemKey);
        if (cartItem) {
          console.log('🔍 Quantity from cart API:', cartItem.quantity);
          return cartItem.quantity;
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to fetch cart for quantity:', error);
    }

    // Method 4: Fallback to 1
    console.log('🔍 Using fallback quantity: 1');
    return 1;
  }

  /**
   * Get the current quantity of this cart item (legacy method - keeping for backward compatibility)
   */
  getItemQuantity() {
    // Try to find quantity from cart item or default to 1
    const quantityInput = document.querySelector(`[data-key="${this.itemKey}"] input[name="updates[]"]`);
    return quantityInput ? parseInt(quantityInput.value) || 1 : 1;
  }

  updateFrequencyDisplay() {
    console.log('🎨 updateFrequencyDisplay called - currentFrequency:', this.currentFrequency);
    
    // Update frequency display in toggle text
    const frequencyDays = this.querySelector('.cart-subscribe-save__frequency-days');
    if (frequencyDays) {
      const daysText = this.currentFrequency === '30' ? 
        (this.translations.thirtyDays || '30 days') : 
        (this.translations.sixtyDays || '60 days');
      frequencyDays.textContent = daysText;
      console.log('✅ Updated frequency display to:', daysText);
      console.log('🔍 Translation values:', {
        currentFrequency: this.currentFrequency,
        thirtyDays: this.translations.thirtyDays,
        sixtyDays: this.translations.sixtyDays,
        datasetText30Days: this.dataset.text30Days,
        datasetText60Days: this.dataset.text60Days
      });
    }

    // Update discount percentage based on frequency
    const discountElement = this.querySelector('.cart-subscribe-save__discount');
    if (discountElement) {
      const discountPercent = this.currentFrequency === '30' ? '-15%' : '-10%';
      discountElement.textContent = discountPercent;
      console.log('✅ Updated discount display to:', discountPercent);
    }

    // Update dropdown active state
    this.updateDropdownActiveState();

    // Note: Actual discounts are handled automatically by Recharge selling plans
    // This is just for UI display purposes
  }

  /**
   * Check subscription status from current cart state
   */
  checkSubscriptionStatus() {
    console.log('🔍 checkSubscriptionStatus called');
    
    const checkbox = this.querySelector('.cart-subscribe-save__checkbox');
    if (!checkbox) {
      console.warn('⚠️ Checkbox not found');
      return;
    }

    // Check if this item is already subscribed based on data attribute
    this.isSubscription = this.isAlreadySubscribed;
    
    if (this.isSubscription) {
      console.log('✅ Item is already subscribed');
      checkbox.checked = true;
      this.updateSubscriptionDisplay();
    } else {
      console.log('ℹ️ Item is not subscribed');
      checkbox.checked = false;
      // Update the subscribe text and frequency display with translated text
      const textElement = this.querySelector('.cart-subscribe-save__text');
      if (textElement) {
        const firstTextNode = textElement.childNodes[0];
        if (firstTextNode && firstTextNode.nodeType === Node.TEXT_NODE) {
          firstTextNode.textContent = this.translations.subscribe;
        }
      }
      
      // Update frequency display with translated text
      this.updateFrequencyDisplay();
    }

    // Re-bind events to ensure they work after any DOM updates
    this.bindEvents();
    
    console.log('✅ Subscription status checked and updated');
  }

  /**
   * Update the display to show subscription state
   */
  updateSubscriptionDisplay() {
    console.log('�� updateSubscriptionDisplay called - isSubscription:', this.isSubscription);
    
    const container = this.querySelector('.cart-subscribe-save__container');
    if (!container) {
      console.warn('⚠️ Container not found for subscription display update');
      return;
    }

    // Add visual indicator that this is a subscription
    this.classList.add('is-subscription');
    container.classList.add('is-subscription');
    
    // Update the text to show it's active
    const textElement = this.querySelector('.cart-subscribe-save__text');
    if (textElement) {
      const subscribeText = this.isSubscription ? 
        (this.translations.subscribed || 'Subscribed') : 
        (this.translations.subscribe || 'Subscribe');
      
      // Update just the text node, preserving the frequency toggle
      const firstTextNode = textElement.childNodes[0];
      if (firstTextNode && firstTextNode.nodeType === Node.TEXT_NODE) {
        firstTextNode.textContent = subscribeText;
      } else {
        // If no text node found, update the whole content but preserve the toggle
        const frequencyToggle = textElement.querySelector('.cart-subscribe-save__frequency-toggle');
        const icon = textElement.querySelector('.cart-subscribe-save__icon');
        const discount = textElement.querySelector('.cart-subscribe-save__discount');
        
        textElement.innerHTML = subscribeText;
        if (frequencyToggle) textElement.appendChild(frequencyToggle);
        if (icon) textElement.appendChild(icon);
        if (discount) textElement.appendChild(discount);
      }
      
      console.log('✅ Updated subscription text to:', subscribeText);
    }

    // Check the checkbox
    const checkbox = this.querySelector('.cart-subscribe-save__checkbox');
    if (checkbox && !checkbox.checked) {
      checkbox.checked = true;
      console.log('✅ Checked subscription checkbox');
    }

    // Update frequency display
    this.updateFrequencyDisplay();
    
    console.log('✅ Subscription display updated');
  }

  setLoadingState(loading) {
    this.isProcessing = loading;
    this.classList.toggle('loading', loading);
    
    const checkbox = this.querySelector('.cart-subscribe-save__checkbox');
    if (checkbox) {
      checkbox.disabled = loading;
    }

    // Disable frequency buttons during loading
    const frequencyOptions = this.querySelectorAll('.cart-subscribe-save__frequency-option');
    frequencyOptions.forEach(option => {
      option.disabled = loading;
    });

    const container = this.querySelector('.cart-subscribe-save__container');
    const loadingDiv = this.querySelector('.cart-subscribe-save__loading');
    
    if (container && loadingDiv) {
      container.style.display = loading ? 'none' : 'flex';
      loadingDiv.style.display = loading ? 'flex' : 'none';
    }
  }

  showFrequencyUpdateSuccess() {
    // Show a subtle success message without replacing the entire content
    const container = this.querySelector('.cart-subscribe-save__container');
    if (container) {
      // Add a temporary success indicator
      const successIndicator = document.createElement('div');
      successIndicator.className = 'cart-subscribe-save__success-indicator';
      successIndicator.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#4CAF50" stroke-width="2" fill="none"/>
        </svg>
        <span style="color: #4CAF50; font-size: 0.75rem; font-weight: 500;">${this.translations.successFrequencyUpdated}</span>
      `;
      successIndicator.style.cssText = `
        position: absolute;
        top: -25px;
        right: 0;
        display: flex;
        align-items: center;
        gap: 4px;
        background: white;
        padding: 4px 8px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        z-index: 1001;
        animation: fadeInOut 2s ease-in-out forwards;
      `;
      
      // Add animation styles if not already added
      if (!document.querySelector('#cart-subscribe-success-animation')) {
        const style = document.createElement('style');
        style.id = 'cart-subscribe-success-animation';
        style.textContent = `
          @keyframes fadeInOut {
            0% { opacity: 0; transform: translateY(10px); }
            20% { opacity: 1; transform: translateY(0); }
            80% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-10px); }
          }
        `;
        document.head.appendChild(style);
      }
      
      container.style.position = 'relative';
      container.appendChild(successIndicator);
      
      // Remove after animation
      setTimeout(() => {
        if (successIndicator.parentNode) {
          successIndicator.remove();
        }
      }, 2000);
    }
  }

  /**
   * Show success message for subscription creation
   */
  showSuccess() {
    console.log('🎉 showSuccess called');
    
    // Create success indicator
    const successIndicator = document.createElement('span');
    successIndicator.style.cssText = `
      color: #4CAF50;
      font-size: 0.75rem;
      font-weight: 500;
      margin-left: 8px;
      opacity: 1;
      transition: opacity 0.3s ease;
    `;
    successIndicator.textContent = this.translations.successSubscribed;
    
    // Add to container
    const container = this.querySelector('.cart-subscribe-save__container');
    if (container) {
      container.appendChild(successIndicator);
      
      // Fade out after delay
      setTimeout(() => {
        successIndicator.style.opacity = '0';
        setTimeout(() => {
          if (successIndicator.parentNode) {
            successIndicator.parentNode.removeChild(successIndicator);
          }
        }, 300);
      }, 2000);
    }
  }

  /**
   * Show success message for subscription removal (one-time purchase)
   */
  showOneTimeSuccess() {
    console.log('🎉 showOneTimeSuccess called');
    
    // Create success indicator
    const successIndicator = document.createElement('span');
    successIndicator.style.cssText = `
      color: #4CAF50;
      font-size: 0.75rem;
      font-weight: 500;
      margin-left: 8px;
      opacity: 1;
      transition: opacity 0.3s ease;
    `;
    successIndicator.textContent = this.translations.successRemoved;
    
    // Add to container
    const container = this.querySelector('.cart-subscribe-save__container');
    if (container) {
      container.appendChild(successIndicator);
      
      // Fade out after delay
      setTimeout(() => {
        successIndicator.style.opacity = '0';
        setTimeout(() => {
          if (successIndicator.parentNode) {
            successIndicator.parentNode.removeChild(successIndicator);
          }
        }, 300);
      }, 2000);
    }
  }

  showError(message) {
    this.classList.add('error');
    const errorDiv = document.createElement('div');
    errorDiv.className = 'cart-subscribe-save__error';
    errorDiv.innerHTML = `
      <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="#ff4444" stroke-width="2" fill="none"/>
        </svg>
        <span style="color: #ff4444; font-weight: 600;">Subscription failed</span>
      </div>
      <p style="margin: 0; font-size: 0.75rem; color: var(--color-text-secondary);">
        ${message || 'Please try again or contact support.'}
      </p>
    `;
    
    this.appendChild(errorDiv);
    
    // Remove error after 5 seconds
    setTimeout(() => {
      this.classList.remove('error');
      errorDiv.remove();
    }, 5000);
  }

  /**
   * Store the original HTML for resetting after success messages
   */
  getOriginalHTML() {
    // This should match the original HTML structure from the Liquid template
    const daysText = this.currentFrequency === '30' ? (this.translations.thirtyDays || '30 jours') : (this.translations.sixtyDays || '60 jours');
    const discountPercent = this.currentFrequency === '30' ? '-15%' : '-10%';
    const subscribeText = this.isSubscription ? (this.translations.subscribed || 'Abonné') : (this.translations.subscribe || "S'abonner");
    
    return `
      <div class="cart-subscribe-save__container">
        <div class="cart-subscribe-save__checkbox-wrapper">
          <label class="cart-subscribe-save__checkbox-label">
            <input 
              type="checkbox" 
              class="cart-subscribe-save__checkbox"
              data-subscription-checkbox
              ${this.isSubscription ? 'checked' : ''}
            >
            <span class="checkmark"></span>
            <span class="cart-subscribe-save__text">
              ${subscribeText}
              <span class="cart-subscribe-save__frequency-toggle" data-frequency-toggle>
                (<span class="cart-subscribe-save__frequency-days">${daysText}</span>)
                <!-- Dropdown menu for frequency selection -->
                <div class="cart-subscribe-save__frequency-dropdown" style="display: none;">
                  <div class="cart-subscribe-save__frequency-option" data-frequency="30">
                    ${this.translations.thirtyDaysDiscount || '30 days (15%)'}
                  </div>
                  <div class="cart-subscribe-save__frequency-option" data-frequency="60">
                    ${this.translations.sixtyDaysDiscount || '60 days (10%)'}
                  </div>
                </div>
              </span>
              <svg class="cart-subscribe-save__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14.3346 8.00033C14.3346 11.4981 11.4991 14.3337 8.0013 14.3337C4.50353 14.3337 1.66797 11.4981 1.66797 8.00033C1.66797 4.50252 4.50353 1.66699 8.0013 1.66699C10.5984 1.66699 12.8304 3.23018 13.8076 5.46699M14.3346 3.66699L14.018 5.78366L12.0013 5.33366" stroke="#020000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="cart-subscribe-save__discount">${discountPercent}</span>
            </span>
          </label>
        </div>
      </div>
      
      <div class="cart-subscribe-save__loading" style="display: none;">
        <div class="cart-subscribe-save__spinner"></div>
        <span>${this.translations.switching || 'Loading...'}</span>
      </div>
    `;
  }

  /**
   * Format price for display (simple version)
   */
  formatPrice(priceInCents) {
    const price = priceInCents / 100;
    return `$${price.toFixed(2)}`;
  }

  /**
   * Refresh only the cart instead of the whole page (optimized for speed)
   */
  refreshCart() {
    console.log('🔄 Refreshing cart (fast mode)...');
    
    try {
      // Method 1: Use the theme's existing refreshCart function (fastest and most reliable)
      if (typeof window.refreshCart === 'function') {
        console.log('✅ Using theme\'s window.refreshCart() - fastest method');
        window.refreshCart();
        return;
      }
      
      // Method 2: Dispatch cart events for other components
      console.log('✅ Dispatching cart events');
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      // Method 3: Manual cart refresh (only if needed)
      this.manualCartRefresh();
      
    } catch (error) {
      console.error('❌ Cart refresh failed:', error);
      // Remove the page reload fallback - it's too slow
      // Instead, just show an error message
      this.showError('Cart refresh failed. Please refresh the page manually.');
    }
  }

  /**
   * Manual cart refresh using the same approach as the theme's refreshCart
   */
  async manualCartRefresh() {
    try {
      console.log('🔄 Manual cart refresh...');
      
      // Fetch the helper-cart section (same as theme's refreshCart)
      const response = await fetch(`${window.location.href}?section_id=helper-cart`);
      if (!response.ok) throw new Error('Failed to fetch cart section');
      
      const text = await response.text();
      const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
      
      // Update cart form
      const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm')?.innerHTML;
      const cartItems = document.getElementById('AjaxCartForm');
      
      if (cartItems && cartFormInnerHTML) {
        cartItems.innerHTML = cartFormInnerHTML;
        
        // Re-initialize cart items if the method exists
        if (typeof cartItems.ajaxifyCartItems === 'function') {
          cartItems.ajaxifyCartItems();
        }
        
        console.log('✅ Cart form updated');
      }
      
      // Update cart count in header
      const cartCountElement = cartItems?.querySelector('[data-cart-count]');
      if (cartCountElement) {
        document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
          elm.textContent = cartCountElement.textContent;
        });
      }
      
      // Update cart total in header
      const cartTotalElement = cartItems?.querySelector('[data-cart-total]');
      if (cartTotalElement) {
        document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
          elm.textContent = cartTotalElement.textContent;
        });
      }
      
      // Update cart subtotal
      const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal')?.innerHTML;
      const cartSubtotal = document.getElementById('AjaxCartSubtotal');
      if (cartSubtotal && cartSubtotalInnerHTML) {
        cartSubtotal.innerHTML = cartSubtotalInnerHTML;
      }
      
      // Show cart sidebar if it exists
      const cartSidebar = document.querySelector('[data-js-site-cart-sidebar]');
      if (cartSidebar && typeof cartSidebar.show === 'function') {
        cartSidebar.show();
      }
      
      console.log('✅ Manual cart refresh completed');
      
    } catch (error) {
      console.error('❌ Manual cart refresh failed:', error);
      throw error;
    }
  }
}

// Register the custom element
if (!customElements.get('cart-subscribe-save')) {
  customElements.define('cart-subscribe-save', CartSubscribeSave);
}

// Initialize existing elements on page load
document.addEventListener('DOMContentLoaded', () => {
  console.log('🔄 Initializing Cart Subscribe & Save widgets...');
  const widgets = document.querySelectorAll('cart-subscribe-save:not(.initialized)');
  console.log(`📦 Found ${widgets.length} subscription widgets to initialize`);
  
  widgets.forEach((widget, index) => {
    console.log(`🔄 Initializing widget ${index + 1}:`, widget);
    widget.classList.add('initialized');
  });
});

// Re-initialize when cart is updated via AJAX
document.addEventListener('cart:updated', () => {
  console.log('🔄 Cart updated, re-initializing subscription widgets...');
  const widgets = document.querySelectorAll('cart-subscribe-save:not(.initialized)');
  widgets.forEach(widget => {
    widget.classList.add('initialized');
  });
});

// Also listen for Shopify cart drawer updates
document.addEventListener('cart:refresh', () => {
  console.log('🔄 Cart refreshed, re-initializing subscription widgets...');
  setTimeout(() => {
    const widgets = document.querySelectorAll('cart-subscribe-save:not(.initialized)');
    widgets.forEach(widget => {
      widget.classList.add('initialized');
    });
  }, 100);
}); 